import { useEffect, useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CustomerGroupSelect } from "@/components/shared/CustomerGroupSelect";
import { Client, clientsApi } from "@/lib/api";
import { getCustomerRegistrationErrorMessage } from "@/lib/customerRegistrationError";

interface Props {
  client: Client;
  clientQueryKey: readonly unknown[];
}

/**
 * Grupo do cliente com edição (customerGroupAddressFeature): mostra o grupo atual e o lápis abre o select, com
 * aplicar e cancelar. Aplicar grava o grupo e também o texto legado client_customer_group no backend (dual-write).
 */
export function ClientCustomerGroupField({ client, clientQueryKey }: Props) {
  const selectId = useId();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [groupId, setGroupId] = useState<number | null>(client.customerGroupId ?? null);

  useEffect(() => {
    setGroupId(client.customerGroupId ?? null);
  }, [client.customerGroupId]);

  const mutation = useMutation({
    mutationFn: (customerGroupId: number) =>
      clientsApi.update(client.clientId!, { customerGroupId, comment: "Grupo do cliente alterado pelo backoffice." }),
    onSuccess: async () => {
      toast.success("Grupo do cliente atualizado.");
      setIsEditing(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientQueryKey }),
        queryClient.invalidateQueries({ queryKey: ["clients"] }),
      ]);
    },
    onError: (error) => {
      toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível alterar o grupo do cliente."));
    },
  });

  const changed = groupId != null && groupId !== (client.customerGroupId ?? null);
  const group = client.customerGroup;

  const cancel = () => {
    setGroupId(client.customerGroupId ?? null);
    setIsEditing(false);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={isEditing ? selectId : undefined} className="text-sm text-muted-foreground">Grupo do Cliente</Label>
      {isEditing ? (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 sm:max-w-64">
            <CustomerGroupSelect id={selectId} value={groupId} onChange={setGroupId} disabled={mutation.isPending} />
          </div>
          <Button
            size="icon"
            aria-label="Aplicar grupo"
            title="Aplicar grupo"
            disabled={!changed || mutation.isPending || !client.clientId}
            onClick={() => groupId != null && mutation.mutate(groupId)}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button
            size="icon"
            variant="outline"
            aria-label="Cancelar alteração do grupo"
            title="Cancelar"
            disabled={mutation.isPending}
            onClick={cancel}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          {group ? (
            <p className="font-medium">
              {group.name}
              {group.name !== group.code && <span className="font-mono text-muted-foreground"> ({group.code})</span>}
            </p>
          ) : client.customerGroupId && client.clientCustomerGroup ? (
            // Sem o resumo do grupo na resposta, o texto legado tem o código do grupo vinculado (dual-write).
            <p className="font-mono font-medium">{client.clientCustomerGroup}</p>
          ) : (
            <p className="text-muted-foreground">Sem grupo vinculado</p>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            aria-label="Alterar grupo do cliente"
            title="Alterar grupo"
            disabled={!client.clientId}
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      )}
      {!client.customerGroupId && client.clientCustomerGroup && (
        <p className="text-xs text-muted-foreground">
          Texto legado: <span className="font-mono">{client.clientCustomerGroup}</span> (sem grupo vinculado)
        </p>
      )}
    </div>
  );
}
